import {clsx} from "clsx";
import type {RowData} from "@tanstack/react-table";
import {Pagination} from "@/primitives/Pagination";
import {useDataTableContext} from "./DataTableRoot";
import styles from "./DataTable.module.css";

/**
 * Thin adapter feeding the table's paging state into the Pagination
 * primitive
 */
function DataTablePagination({
	className,
	pageSizes,
	siblingCount,
	...props
}: React.ComponentProps<"div"> & {
	/** Show a page-size `<select>` with these options. Omit to hide it. */
	pageSizes?: readonly number[];
	/** Pages shown around the current one, forwarded to `Pagination.Items`. */
	siblingCount?: number;
}) {
	const {table, loading} = useDataTableContext<RowData>("Pagination");
	const {pageIndex, pageSize} = table.state.pagination;
	const pageCount = table.getPageCount();

	if (!table.options.manualPagination && !table.options.features.paginatedRowModel) {
		return null;
	}

	// include the current pageSize so the controlled <select> always has a matching <option>
	const sizeOptions = pageSizes && pageSizes.length > 0 ? [...new Set([...pageSizes, pageSize])].sort((a, b) => a - b) : [];

	return (
		<div data-slot="data-table-pagination" className={clsx(styles.pagination, className)} {...props}>
			{sizeOptions.length > 0 && (
				<label className={styles.pageSize}>
					{messages["datatable.pageSize"]}
					<select
						className={clsx("form-control input-sm", styles.pageSizeSelect)}
						value={pageSize}
						disabled={loading}
						onChange={(event) => table.setPageSize(Number(event.target.value))}
					>
						{sizeOptions.map((size) => (
							<option key={size} value={size}>
								{size}
							</option>
						))}
					</select>
				</label>
			)}

			{/* not a live region: the loading region in Content already announces changes,
			    and focus stays on the pagination controls when the page changes */}
			<span data-slot="data-table-page-info" className={styles.pageInfo}>
				{messages.withArguments("datatable.pageInfo", pageIndex + 1, Math.max(pageCount, 1))}
			</span>

			<Pagination.Root
				page={pageIndex + 1}
				pageCount={Math.max(pageCount, 1)}
				onPageChange={(page) => table.setPageIndex(page - 1)}
				disabled={loading}
				getPageLabel={(page) => messages.withArguments("datatable.paginate.goToPage", page)}
				aria-label={messages["datatable.paginate.label"]}
			>
				<Pagination.Previous>{messages["datatable.paginate.previous"]}</Pagination.Previous>
				<Pagination.Items siblingCount={siblingCount} />
				<Pagination.Next>{messages["datatable.paginate.next"]}</Pagination.Next>
			</Pagination.Root>
		</div>
	);
}

export {DataTablePagination};
